/**
 * Track-local Flight School API surface.
 *
 * iOS ref: client-native/src/api/Api.ts (getFlightSchoolProgress /
 * postFlightSchoolModuleComplete / deleteFlightSchoolModuleCompletion /
 * completeTutorialItem). These endpoints are NOT in the shared `@/api/Api.ts`
 * yet, so per the ownership contract a NEW endpoint goes in a track-local file.
 *
 * Endpoints (server routes.go):
 *   GET    /auth/flightschool/progress            → { completedModules: string[] }
 *   POST   /auth/flightschool/complete  { moduleId }
 *   DELETE /auth/flightschool/progress/:moduleId  (dev reset)
 *   POST   /auth/tutorial/complete      { itemId }  (placement_test on cognates-1)
 */

import { AxiosError } from "axios";
import { axiosInstance } from "@/api/Api";
import {
  CompleteFlightSchoolModuleRequest,
  GetFlightSchoolProgressResponse,
  GenericMessageResponse,
  PostCompleteTutorialItemRequest,
  TutorialItemID,
  FlightSchoolModuleConfigPublicResponse,
} from "@shared_types";

function authHeaders(authToken: string) {
  return { headers: { Authorization: `Bearer ${authToken}` } };
}

/**
 * GET /public/flightschool/config (no auth) — the backend-authored module config
 * that OVERRIDES the hardcoded lessons (admin UI at /flight-school-config).
 * Mirrors iOS getFlightSchoolModuleConfig.
 * The public config is OPTIONAL — when there's no active published config the
 * hardcoded lessons/*.ts remain the source of truth — so a missing/failed
 * response resolves to `undefined` rather than throwing.
 */
export async function getFlightSchoolModuleConfig(): Promise<
  FlightSchoolModuleConfigPublicResponse | undefined
> {
  try {
    const response =
      await axiosInstance.get<FlightSchoolModuleConfigPublicResponse>(
        "/public/flightschool/config",
      );
    return response.data;
  } catch {
    return undefined;
  }
}

export async function getFlightSchoolProgress({
  authToken,
}: {
  authToken: string;
}): Promise<GetFlightSchoolProgressResponse> {
  try {
    const response = await axiosInstance.get<GetFlightSchoolProgressResponse>(
      "/auth/flightschool/progress",
      authHeaders(authToken),
    );
    return response.data;
  } catch (error) {
    throw error as AxiosError;
  }
}

export async function postFlightSchoolModuleComplete({
  authToken,
  data,
}: {
  authToken: string;
  data?: CompleteFlightSchoolModuleRequest;
}): Promise<GenericMessageResponse> {
  const response = await axiosInstance.post<GenericMessageResponse>(
    "/auth/flightschool/complete",
    data,
    authHeaders(authToken),
  );
  return response.data;
}

export async function deleteFlightSchoolModuleCompletion({
  authToken,
  data,
}: {
  authToken: string;
  data?: CompleteFlightSchoolModuleRequest;
}): Promise<GenericMessageResponse> {
  const response = await axiosInstance.delete<GenericMessageResponse>(
    `/auth/flightschool/progress/${data?.moduleId}`,
    authHeaders(authToken),
  );
  return response.data;
}

export async function completeTutorialItem({
  authToken,
  data,
}: {
  authToken: string;
  data?: TutorialItemID;
}): Promise<GenericMessageResponse> {
  const body: PostCompleteTutorialItemRequest = {
    itemId: data ?? "placement_test",
  };
  const response = await axiosInstance.post<GenericMessageResponse>(
    "/auth/tutorial/complete",
    body,
    authHeaders(authToken),
  );
  return response.data;
}
